import {
  DtoProjectType,
  type DtoProjectType as DtoProjectTypeValue,
} from '@/shared/constants';
import {
  createSavedEditorStateFromPayload,
  isSavedEditorStatePayload,
  type SavedEditorStateFromPayload,
} from '@/features/remotion-editor/editor/utils/saved-editor-state-payload';

const AGENT_WORKFLOW_EDITOR_SOURCE_ASSETS_KEY =
  'agentWorkflowEditorSourceAssets';
const AGENT_WORKFLOW_EDITOR_SOURCE_ITEMS_KEY = 'agentWorkflowEditorSourceItems';
const AGENT_WORKFLOW_EDITOR_SOURCE_TRACKS_KEY =
  'agentWorkflowEditorSourceTracks';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const hasEditorSourceLayoutSeeds = (payload: Record<string, unknown>) =>
  isRecord(payload[AGENT_WORKFLOW_EDITOR_SOURCE_ASSETS_KEY]) &&
  isRecord(payload[AGENT_WORKFLOW_EDITOR_SOURCE_ITEMS_KEY]) &&
  Array.isArray(payload[AGENT_WORKFLOW_EDITOR_SOURCE_TRACKS_KEY]);

export type SavedSsulResetState = SavedEditorStateFromPayload;

/**
 * 에디터 상태를 이력 payload 로 보관하는(= 위저드가 deck 을 bake 해 PUT 하는) 타입.
 * BE `ProjectType.usesDeckEditorHistoryPayload()` 와 같은 집합이며, 둘 중 하나만 바뀌면
 * 초기화가 빈 화면으로 떨어진다 — StoryClipInfo.payload 가 빈 clipInfos 이기 때문이다.
 */
const DECK_EDITOR_HISTORY_PAYLOAD_PROJECT_TYPES: DtoProjectTypeValue[] = [
  DtoProjectType.remotionDeck,
  DtoProjectType.agentRemotionDeck,
  DtoProjectType.agentRemotionDeckScript,
  DtoProjectType.agentRemotionDeckVideoCreator,
  DtoProjectType.agentSsulDeck,
  DtoProjectType.agentPdfStory,
  DtoProjectType.agentLectureDeck,
  DtoProjectType.agentSlideLecture,
];

export const usesDeckEditorHistoryPayload = (
  projectType?: DtoProjectTypeValue,
): boolean =>
  projectType != null &&
  DECK_EDITOR_HISTORY_PAYLOAD_PROJECT_TYPES.includes(projectType);

/** 이력 payload → 에디터 상태. bake 본·저장분 어느 쪽이든 같은 변환을 쓴다. */
export const createResetStateFromHistoryPayload = (
  payload: Record<string, unknown> | undefined | null,
): SavedEditorStateFromPayload | null => {
  if (!payload || !isSavedEditorStatePayload(payload)) return null;
  return createSavedEditorStateFromPayload(payload);
};

export const createSavedSsulResetState = ({
  projectType,
  payload,
}: {
  projectType?: DtoProjectTypeValue;
  payload: Record<string, unknown> | undefined;
}): SavedSsulResetState | null => {
  if (
    projectType !== DtoProjectType.agentSsulShorts ||
    !payload ||
    !isSavedEditorStatePayload(payload) ||
    hasEditorSourceLayoutSeeds(payload)
  ) {
    return null;
  }

  return createSavedEditorStateFromPayload(payload);
};
